import MatchingDetail from './components/MatchingDetail';
import { AdminConsultationPage } from './pages/AdminPage/ConsultationsTab';
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ConfigProvider } from './contexts/ConfigContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';

// Публичные страницы (для iframe)
import MainPage from './pages/MainPage/MainPage';
import GroupPage from './pages/GroupPage/GroupPage';
import ContactFormPage from './pages/ContactFormPage/ContactFormPage';
import UnsubscribePage from './pages/UnsubscribePage/UnsubscribePage';
import PublicProfilePage from './pages/PublicProfilePage/PublicProfilePage';
import AgregatorPage from './pages/AgregatorPage/AgregatorPage';
import TeachersPage from './pages/TeachersPage/TeachersPage';
import MatchingApplicationPage from './pages/MatchingApplicationPage/MatchingApplicationPage';
import TherapistsTermsPage from './pages/TherapistsTermsPage/TherapistsTermsPage';
import TeacherProfilePage from './pages/TeacherProfilePage/TeacherProfilePage';
import MeetPage from './pages/MeetPage/MeetPage';
import JoinMeetPage from './pages/MeetPage/JoinMeetPage';

// Страница входа
import LoginPage from './pages/LoginPage/LoginPage';

// Защищенные страницы
import IndexPage from './pages/IndexPage/IndexPage';
import CalendarPage from './pages/CalendarPage/CalendarPage';
import AdminPage from './pages/AdminPage/AdminPage';
import ProfilePage from './pages/ProfilePage/ProfilePage';
import GroupSchedulePage from './pages/GroupSchedulePage/GroupSchedulePage';
import GroupApplicationsPage from './pages/GroupApplicationsPage/GroupApplicationsPage';
import InstructionsPage from './pages/InstructionsPage/InstructionsPage';
import ManagerApplicationsPage from './pages/ManagerApplicationsPage/ManagerApplicationsPage';
import TherapistSchedulePage from './pages/TherapistSchedulePage/TherapistSchedulePage';
import TherapistBookingsPage, { TherapistConsultationPage, TherapistQuestionPage } from './pages/TherapistBookingsPage/TherapistBookingsPage';
import TherapistPaymentsPage from './pages/TherapistPaymentsPage/TherapistPaymentsPage';
import UserZoomPage from './pages/UserZoomPage/UserZoomPage';
import PublicPaymentPage from './pages/PublicPaymentPage/PublicPaymentPage';
import ArticlesPage from './pages/ArticlesPage/ArticlesPage';
import ArticlePage from './pages/ArticlePage/ArticlePage';
import ArticlesEditorPage from './pages/ArticlesEditorPage/ArticlesEditorPage';
import ArticleEditorPage from './pages/ArticleEditorPage/ArticleEditorPage';
import KnowledgeBasePage from './pages/KnowledgeBasePage/KnowledgeBasePage';
import KnowledgeArticlePage from './pages/KnowledgeArticlePage/KnowledgeArticlePage';
import KnowledgeEditorPage from './pages/KnowledgeEditorPage/KnowledgeEditorPage';
import KnowledgeArticleEditorPage from './pages/KnowledgeArticleEditorPage/KnowledgeArticleEditorPage';
import DirectionPage from './pages/DirectionPage/DirectionPage';
import DirectionsListPage from './pages/DirectionsListPage/DirectionsListPage';
import LegacyGroupRedirectPage from './pages/LegacyGroupRedirectPage/LegacyGroupRedirectPage';

// Создаем клиент React Query
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            retry: 1,
            staleTime: 5 * 60 * 1000, // 5 минут
        },
    },
});

// Компонент для управления классами body
function BodyClassManager() {
    const location = useLocation();
    const isInIframe = window.self !== window.top;
    const isPublicPage = location.pathname.startsWith('/public/') ||
        location.pathname.startsWith('/directions');

    useEffect(() => {
        // Добавляем класс для публичных страниц
        if (isPublicPage) {
            document.body.classList.add('public-page');
        } else {
            document.body.classList.remove('public-page');
        }

        // Добавляем класс для iframe и скрываем скроллбар
        if (isInIframe) {
            document.documentElement.classList.add('iframe-page');
            document.body.style.overflow = 'hidden';
        } else {
            document.documentElement.classList.remove('iframe-page');
            document.body.style.overflow = '';
        }

        return () => {
            document.body.classList.remove('public-page');
            document.documentElement.classList.remove('iframe-page');
            document.body.style.overflow = '';
        };
    }, [location.pathname, isInIframe, isPublicPage]);

    return null;
}

function ArticleIdRedirect() {
    const { id } = useParams();
    return <Navigate to={`/public/articles/${id}`} replace />;
}

function PublicGroupRedirect() {
    const { id } = useParams();
    return <Navigate to={`/g/${id}`} replace />;
}

function HomePage() {
    const { user } = useAuth();
    if (user?.is_therapist && user.role === 'user' && !user.is_teacher && !user.is_knowledge_editor) {
        return <Navigate to="/therapist/bookings" replace />;
    }
    return <IndexPage />;
}

function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <ConfigProvider>
                <AuthProvider>
                    <Router>
                        <BodyClassManager />
                        <Routes>
                            {/* Публичные страницы (для iframe на внешнем сайте) */}
                            <Route path="/public/groups" element={<MainPage />} />
                            <Route path="/g/:id" element={<GroupPage />} />
                            <Route path="/public/group/:id" element={<PublicGroupRedirect />} />
                            {/* Редирект со старых URL: /group/?96929978 → /g/:newId */}
                            <Route path="/group" element={<LegacyGroupRedirectPage />} />
                            <Route path="/group/" element={<LegacyGroupRedirectPage />} />
                            <Route path="/public/application/:groupId" element={<ContactFormPage />} />
                            <Route path="/public/unsubscribe" element={<UnsubscribePage />} />
                            <Route path="/public/profile/:id" element={<PublicProfilePage />} />
                            <Route path="/public/therapists" element={<AgregatorPage />} />
                            <Route path="/public/teachers" element={<TeachersPage />} />
                            <Route path="/public/therapist-matching" element={<MatchingApplicationPage />} />
                            <Route path="/public/therapists-terms" element={<TherapistsTermsPage />} />
                            <Route path="/public/payment" element={<PublicPaymentPage />} />
                            <Route path="/public/teacher" element={<TeacherProfilePage />} />
                            <Route path="/public/teacher/:id" element={<TeacherProfilePage />} />
                            <Route path="/public/articles" element={<ArticlesPage />} />
                            <Route path="/public/articles/:id" element={<ArticlePage />} />
                            <Route path="/directions" element={<DirectionsListPage />} />
                            <Route path="/directions/:id" element={<DirectionPage />} />
                            {/* Совместимость со старым URL списка статей */}
                            <Route path="/articles" element={<Navigate to="/public/articles" replace />} />

                            {/* P2P видеовстречи (без авторизации) */}
                            <Route path="/meet" element={<ProtectedRoute><MeetPage /></ProtectedRoute>} />
                            <Route path="/meet/room" element={<Navigate to="/join-meet" replace />} />
                            <Route path="/join-meet" element={<JoinMeetPage />} />
                            <Route path="/join-meet/:code" element={<JoinMeetPage />} />

                            {/* Страница для менеджера по обучению (публичная с пин-кодом) */}
                            <Route path="/manager/applications" element={<ManagerApplicationsPage />} />

                            {/* Страница входа */}
                            <Route path="/login" element={<LoginPage />} />

                            {/* Защищенные страницы */}
                            <Route
                                path="/"
                                element={
                                    <ProtectedRoute>
                                        <Layout>
                                            <HomePage />
                                        </Layout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/calendar"
                                element={
                                    <ProtectedRoute>
                                        <Layout>
                                            <CalendarPage />
                                        </Layout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/admin"
                                element={
                                    <ProtectedRoute requiredRole="admin">
                                        <Layout>
                                            <AdminPage />
                                        </Layout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/profile"
                                element={
                                    <ProtectedRoute>
                                        <Layout>
                                            <ProfilePage />
                                        </Layout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/group/:id/schedule"
                                element={
                                    <ProtectedRoute>
                                        <Layout>
                                            <GroupSchedulePage />
                                        </Layout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/group/:id/applications"
                                element={
                                    <ProtectedRoute>
                                        <Layout>
                                            <GroupApplicationsPage />
                                        </Layout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/instructions"
                                element={
                                    <ProtectedRoute>
                                        <Layout>
                                            <InstructionsPage />
                                        </Layout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/therapist/schedule"
                                element={
                                    <ProtectedRoute>
                                        <Layout>
                                            <TherapistSchedulePage />
                                        </Layout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route path="/admin/matching/:id" element={<ProtectedRoute requiredRole="admin"><Layout><MatchingDetail admin/></Layout></ProtectedRoute>} /><Route path="/admin/consultations/:id" element={<ProtectedRoute requiredRole="admin"><Layout><AdminConsultationPage /></Layout></ProtectedRoute>} />
                            <Route path="/therapist/bookings/:id" element={<ProtectedRoute><Layout><TherapistConsultationPage /></Layout></ProtectedRoute>} />
                            <Route path="/therapist/messages/:id" element={<ProtectedRoute><Layout><TherapistQuestionPage /></Layout></ProtectedRoute>} />
                            <Route
                                path="/therapist/bookings"
                                element={
                                    <ProtectedRoute>
                                        <Layout>
                                            <TherapistBookingsPage />
                                        </Layout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/therapist/payments"
                                element={
                                    <ProtectedRoute>
                                        <Layout>
                                            <TherapistPaymentsPage />
                                        </Layout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/zoom"
                                element={
                                    <ProtectedRoute>
                                        <Layout>
                                            <UserZoomPage />
                                        </Layout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/articles/editor"
                                element={
                                    <ProtectedRoute>
                                        <Layout>
                                            <ArticlesEditorPage />
                                        </Layout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/articles/editor/new"
                                element={
                                    <ProtectedRoute>
                                        <Layout>
                                            <ArticleEditorPage />
                                        </Layout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/articles/editor/edit/:id"
                                element={
                                    <ProtectedRoute>
                                        <Layout>
                                            <ArticleEditorPage />
                                        </Layout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route path="/articles/:id" element={<ArticleIdRedirect />} />

                            {/* ─── База знаний ─── */}
                            <Route
                                path="/knowledge"
                                element={
                                    <ProtectedRoute>
                                        <Layout>
                                            <KnowledgeBasePage />
                                        </Layout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/knowledge/editor"
                                element={
                                    <ProtectedRoute>
                                        <Layout>
                                            <KnowledgeEditorPage />
                                        </Layout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/knowledge/editor/new"
                                element={
                                    <ProtectedRoute>
                                        <Layout>
                                            <KnowledgeArticleEditorPage />
                                        </Layout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/knowledge/editor/edit/:id"
                                element={
                                    <ProtectedRoute>
                                        <Layout>
                                            <KnowledgeArticleEditorPage />
                                        </Layout>
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/knowledge/:id"
                                element={
                                    <ProtectedRoute>
                                        <Layout>
                                            <KnowledgeArticlePage />
                                        </Layout>
                                    </ProtectedRoute>
                                }
                            />

                            {/* Редирект на главную */}
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                    </Router>
                    <ToastContainer
                        position="top-right"
                        autoClose={3000}
                        hideProgressBar={false}
                        newestOnTop
                        closeOnClick
                        rtl={false}
                        pauseOnFocusLoss
                        draggable
                        pauseOnHover
                    />
                </AuthProvider>
            </ConfigProvider>
        </QueryClientProvider>
    );
}

export default App;
